import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaEye, FaArrowLeft, FaShare } from 'react-icons/fa';
import { api } from '../App';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/posts/${slug}`);
      setPost(data.post);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('blog.linkCopied'));
    }
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('blog.loadingPost')}</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <div className="error-container">
          <h2>{t('blog.postNotFound')}</h2>
          <p>{t('blog.postNotFoundDesc')}</p>
          <Link to="/blog" className="btn btn-primary">
            {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <SEO 
        title={post.title}
        description={post.excerpt || post.meta_description}
        keywords={`${post.category}, crypto market, bitcoin analysis, future money blog`}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          image: post.featured_image,
          publishedAt: post.published_at,
          updatedAt: post.updated_at
        }}
      />
      
      {/* Hero Section */}
      <section className="post-hero">
        <div className="container">
          <motion.div
            className="post-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button 
              className="back-button"
              onClick={() => navigate('/blog')}
            >
              <FaArrowLeft /> {t('blog.backToBlog')}
            </button>
            
            <span className="post-category-tag">{post.category}</span>
            <h1 className="post-title">{post.title}</h1>
            
            <div className="post-meta-bar">
              <span className="meta-item">
                <FaCalendar /> {formatDate(post.published_at)}
              </span>
              <span className="meta-item">
                <FaEye /> {post.views || 0} {t('blog.views')}
              </span>
              <button className="share-button" onClick={handleShare}>
                <FaShare /> {t('blog.share')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Post Content */}
      <section className="post-content-section section">
        <div className="container">
          <motion.article
            className="post-article"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {post.featured_image && (
              <div className="featured-image">
                <img src={post.featured_image} alt={post.title} />
              </div>
            )}
            
            <div 
              className="post-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <div className="post-footer-nav">
              <Link to="/blog" className="btn btn-outline">
                <FaArrowLeft /> {t('blog.backToAllPosts')}
              </Link>
              <button className="btn btn-primary" onClick={handleShare}>
                <FaShare /> {t('blog.shareArticle')}
              </button>
            </div>
          </motion.article>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
